/**
 * /el/guides/qr-code-gamou-fotografies
 *
 * The Greek counterpart of `wedding-qr-code-for-photos.en.js` — the MECHANISM
 * half of the Greek photo pair.
 *
 * ── Why the split is by purpose and not by keyword ───────────────────────
 * Greek search intent around guest photos and around QR codes overlaps almost
 * completely: «πώς να μαζέψω φωτογραφίες από τους καλεσμένους» and «QR code
 * γάμου» return much the same results, because the QR framing has already won
 * the category here. Two Greek articles chasing that one query would make
 * Agapaé its own only competitor on its most distinctive feature.
 *
 * So the pair is divided by what each article is FOR:
 *
 *   - `how-to-collect-wedding-photos-from-guests.el.js` owns the decision —
 *     the four options, what each costs a guest, what happens to the files.
 *   - THIS article owns the mechanism — what a scan actually does, where the
 *     code goes, what to test before the day.
 *
 * This file originally carried a merged four-option comparison, because no
 * Greek collection guide existed to hold it. That section was removed when the
 * collection guide was written, and replaced by a short bridge and one link.
 * Do not take the comparison back: if both articles answer "which method should
 * I use", one of them is redundant and they compete for the same query.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * Inherited whole from both English modules, and if anything more important
 * here: the Greek market for this is crowded with vendors making exactly the
 * claims below, and matching them is not an option.
 *
 * The QR code carries an ADDRESS and nothing else. Every behaviour a reader
 * might attribute to "the QR code" — what a guest sees, what media is accepted,
 * who can get in — belongs to the destination behind it and differs between
 * services. Hence «ανάλογα με την υπηρεσία» throughout.
 *
 * Banned outright: exact print dimensions, guaranteed scanning, "more photos",
 * unlimited uploads, permanent storage, automatic or live uploading, every
 * phone behaving identically, and QR codes being BETTER than links rather than
 * more convenient in a room. Placement is a principle with examples, never a
 * ranking — no couple's venue was measured. No quantified participation claim
 * of any kind: no multiples, no percentage of guests, no photo counts.
 *
 * On messaging apps: they MAY compress or process media depending on the
 * platform and on how the sender shared it. Never stated as a certainty, and a
 * reader whose family group chat worked fine is not doing it wrong.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * The same six the English pair established, and nothing beyond them:
 *
 *   - the guest gallery is part of every Agapaé plan, with different limits
 *     on each  (the note) — NOT "photo and video": Blossom is photos only
 *   - guests upload photos and videos to the wedding gallery (videos are
 *     plan-dependent)
 *   - they reach it through the event's guest-facing upload flow
 *   - that flow opens from a QR code or a link
 *   - the gallery is private to the event rather than a public feed
 *   - the plan sets the limits: how many guests can upload, how much the
 *     gallery holds, how long the upload window runs, and whether guests can
 *     add videos
 *
 * Never: «χωρίς λογαριασμό», «χωρίς εφαρμογή» as a guarantee, original quality
 * on every plan. The site's product page has its own approved wording for the
 * guest experience; a guide is not the place to restate it as a promise. Where
 * the mechanism matters, this article says the destination opens in the phone's
 * browser, which is what actually happens and asserts nothing further.
 */

const weddingQrCodeForPhotosEl = {
  slug: "qr-code-gamou-fotografies",

  title: "QR code γάμου για φωτογραφίες: πώς λειτουργεί",
  metaTitle: "QR code γάμου για φωτογραφίες: πώς λειτουργεί | Agapaé",
  metaDescription:
    "Πώς δουλεύει το QR code γάμου για τις φωτογραφίες των καλεσμένων: τι γίνεται όταν το σκανάρουν, πού να το βάλετε, βίντεο, ιδιωτικότητα και τι να δοκιμάσετε πριν.",

  eyebrow: "Φωτογραφίες γάμου",

  hero: {
    // The same photograph the English guide carries, described in Greek. The
    // alt describes the picture, not the keyword — there is no phone and no
    // printed code in the frame, and saying otherwise would be a lie told to
    // exactly the readers who cannot see it.
    src: "/assets/images/lovely_couple_003.jpeg",
    alt: "Ασπρόμαυρη φωτογραφία ζευγαριού που κόβει την τούρτα του γάμου στη δεξίωση, με γιρλάντες από μικρά φωτάκια από πάνω, λουλούδια στο τραπέζι μπροστά και κόσμο να κινείται στο βάθος",
    width: 1280,
    height: 714,
  },

  intro: [
    `Στο τέλος της βραδιάς οι καλεσμένοι σας κουβαλούν εκατοντάδες φωτογραφίες από τον γάμο σας.`,
    `Αυτό που δεν έχουν είναι πού να τις στείλουν.`,
    `Ο φωτογράφος καταγράφει την ιστορία που σχεδιάσατε. Οι καλεσμένοι καταγράφουν όλα τα υπόλοιπα, από εκεί που τυχαίνει να στέκονται. Οι περισσότερες από αυτές τις φωτογραφίες δεν φτάνουν ποτέ στο ζευγάρι: μένουν σε κινητά, κάποιες περνούν από ένα group, και οι υπόλοιπες χάνονται σε μια συζήτηση που κύλησε.`,
    `Αυτό ακριβώς το μικρό, πρακτικό πρόβλημα λύνει το QR code.`,
    `Δεν μπορείτε να πείτε μια διεύθυνση δυνατά σε μια αίθουσα με μουσική, και κανείς δεν θέλει να την πληκτρολογήσει κρατώντας ένα ποτήρι.`,
    `Δείτε τι κάνει στην πραγματικότητα ένα QR code γάμου, τι συμβαίνει όταν το σκανάρει ένας καλεσμένος και τι αξίζει να ελέγξετε πριν την ημέρα του γάμου.`,
  ],

  sections: [
    {
      heading: "Τι είναι το QR code γάμου για φωτογραφίες",
      content: [
        `Είναι ένας κωδικός που, όταν τον σκανάρει κάποιος με την κάμερα του κινητού, ανοίγει μια συγκεκριμένη διεύθυνση.`,
        `Και μόνο αυτό.`,
        `Αξίζει να το κρατήσετε αυτό, γιατί εξηγεί τα πάντα παρακάτω: το QR code δεν αποθηκεύει φωτογραφίες, δεν έχει λογαριασμούς, δεν έχει δικαιώματα πρόσβασης και δεν «μαζεύει» τίποτα. Είναι ο δρόμος, όχι ο προορισμός.`,
        `Ό,τι βλέπει ο καλεσμένος αφού το σκανάρει, τι αρχεία γίνονται δεκτά, πόσο εύκολο είναι το ανέβασμα και ποιος μπορεί να δει το αποτέλεσμα — όλα αυτά ανήκουν στη σελίδα πίσω από τον κωδικό και διαφέρουν από υπηρεσία σε υπηρεσία.`,
        `Όταν λοιπόν συγκρίνετε λύσεις, μη συγκρίνετε QR codes. Συγκρίνετε προορισμούς.`,
      ],
    },
    {
      heading: "Το QR code είναι ένας τρόπος ανάμεσα σε άλλους",
      content: [
        `Πριν μπείτε στη μηχανική του, αξίζει να ξέρετε ότι το QR code δεν είναι μέθοδος συλλογής. Είναι τρόπος να φτάσει κανείς γρήγορα σε έναν προορισμό.`,
        `Ο προορισμός είναι η απόφαση. Κοινόχρηστο άλμπουμ, group στο κινητό, αίτημα μετά τον γάμο ή μια σελίδα ανεβάσματος — το καθένα ζητά διαφορετικά πράγματα από τους καλεσμένους και αφήνει διαφορετικό αποτέλεσμα στο τέλος.`,
        {
          type: "p",
          text: [
            `Αν δεν έχετε διαλέξει ακόμη, ο οδηγός `,
            {
              text: "Πώς θα μαζέψετε τις φωτογραφίες των καλεσμένων από τον γάμο σας",
              href: "/guides/how-to-collect-wedding-photos-from-guests",
            },
            ` συγκρίνει τους τέσσερις τρόπους και το τι κοστίζει ο καθένας.`,
          ],
        },
        `Το υπόλοιπο αυτού του κειμένου υποθέτει ότι έχετε έναν προορισμό και θέλετε οι καλεσμένοι να τον ανοίγουν χωρίς να πληκτρολογήσουν τίποτα.`,
      ],
    },
    {
      heading: "Τι συμβαίνει όταν ένας καλεσμένος σκανάρει τον κωδικό",
      content: [
        `Η διαδρομή είναι σχεδόν πάντα η ίδια:`,
        {
          type: "steps",
          items: [
            `Ο καλεσμένος στρέφει την κάμερα του κινητού στον κωδικό.`,
            `Εμφανίζεται μια ειδοποίηση με τη διεύθυνση.`,
            `Την πατάει και η σελίδα ανοίγει στον browser του κινητού.`,
            `Διαλέγει φωτογραφίες ή βίντεο από το κινητό του.`,
            `Τα ανεβάζει και βλέπει μια επιβεβαίωση.`,
          ],
        },
        `Τα πιο πρόσφατα κινητά διαβάζουν QR codes απευθείας από την κάμερα. Παλαιότερες συσκευές μπορεί να χρειάζονται κάποιο βήμα παραπάνω, και καλό είναι να το ξέρετε από πριν αντί να το ανακαλύψετε στη δεξίωση.`,
        `Από το τρίτο βήμα και μετά, τίποτα δεν εξαρτάται από το QR code. Εξαρτάται από τη σελίδα.`,
      ],
    },
    {
      heading: "Πού να βάλετε το QR code",
      content: [
        `Η αρχή είναι απλή: εκεί που οι καλεσμένοι έχουν ήδη το κινητό στο χέρι και μια στιγμή ελεύθερη.`,
        `Σημεία που δουλεύουν συνήθως καλά:`,
        {
          type: "list",
          items: [
            `στα τραπέζια της δεξίωσης, σε ένα μικρό καρτελάκι`,
            `σε μια πινακίδα στην είσοδο του χώρου`,
            `κοντά στο βιβλίο ευχών ή στο σημείο με τις μπομπονιέρες`,
            `κοντά στο μπαρ`,
            `μέσα στο προσκλητήριο ή στη σελίδα του γάμου σας`,
            `σε ένα μήνυμα προς τους καλεσμένους την επόμενη μέρα`,
          ],
        },
        `Δεν χρειάζεται να τα κάνετε όλα. Δύο ή τρία καλά σημεία είναι αρκετά, και ένα από αυτά καλό είναι να μην απαιτεί από τον καλεσμένο να σηκωθεί.`,
        `Το σημείο δίπλα στο τραπέζι έχει ένα πλεονέκτημα που δεν είναι προφανές: οι άνθρωποι κοιτάζουν το κινητό τους ούτως ή άλλως ανάμεσα στα πιάτα. Εκεί ο κωδικός δεν διακόπτει τίποτα.`,
        {
          type: "note",
          text: `Σκεφτείτε και τον φωτισμό. Ένα καρτελάκι σε σκοτεινό σημείο ή πίσω από μια σύνθεση λουλουδιών δεν σκανάρεται εύκολα, όσο ωραίο κι αν είναι.`,
        },
      ],
    },
    {
      heading: "Πώς να το εξηγήσετε στους καλεσμένους",
      content: [
        `Ένας κωδικός χωρίς λέξεις δίπλα του είναι ένας κωδικός που οι μισοί θα προσπεράσουν.`,
        `Πείτε τρία πράγματα, με τη σειρά: τι είναι, τι πρέπει να κάνουν, και τι θα γίνει μετά.`,
        `Για παράδειγμα:`,
        `«Σκανάρετε και ανεβάστε τις φωτογραφίες σας από τη βραδιά — θα μπουν όλες μαζί στο άλμπουμ μας.»`,
        `Αποφύγετε ένα σκέτο «Ανεβάστε» χωρίς συνέχεια, και αποφύγετε τις οδηγίες σε βήματα. Κανείς δεν διαβάζει εγχειρίδιο σε δεξίωση.`,
        `Αν κάποιοι από τους καλεσμένους σας δεν είναι εξοικειωμένοι με τα κινητά, βοηθάει να το πει κάποιος μία φορά φωναχτά — ο DJ ή όποιος συντονίζει τη βραδιά. Μία φορά, όχι τρεις.`,
      ],
    },
    {
      heading: "Δοκιμάστε τον κωδικό πριν τον γάμο",
      content: [
        `Είναι το πιο σύντομο κομμάτι αυτού του οδηγού και το πιο χρήσιμο.`,
        `Πριν τυπώσετε οτιδήποτε, ελέγξτε:`,
        {
          type: "steps",
          items: [
            `Σκανάρετε τον κωδικό από τυπωμένο χαρτί, όχι από οθόνη.`,
            `Δοκιμάστε τον με ένα iPhone και με ένα Android.`,
            `Ανεβάστε μια πραγματική φωτογραφία, μέχρι το τέλος.`,
            `Ανεβάστε και ένα βίντεο, αν σας ενδιαφέρουν τα βίντεο.`,
            `Δοκιμάστε τον με δεδομένα κινητού, όχι μόνο με Wi-Fi.`,
            `Ζητήστε από κάποιον που δεν ξέρει τίποτα για τον γάμο να το κάνει χωρίς βοήθεια.`,
          ],
        },
        `Το τελευταίο βήμα βρίσκει τα περισσότερα προβλήματα.`,
        `Ελέγξτε επίσης το δίκτυο στον ίδιο τον χώρο. Μια αίθουσα σε υπόγειο ή ένα κτήμα έξω από την πόλη μπορεί να έχει αδύναμο σήμα, και τότε το πιο ρεαλιστικό είναι οι καλεσμένοι να ανεβάσουν αργότερα το ίδιο βράδυ ή την επόμενη μέρα.`,
      ],
    },
    {
      heading: "QR code ή απλός σύνδεσμος;",
      content: [
        `Δεν είναι αντίπαλα. Οδηγούν στην ίδια διεύθυνση.`,
        `Ο σύνδεσμος είναι πιο βολικός εκεί που ο καλεσμένος βλέπει ήδη οθόνη: σε ένα μήνυμα, σε ένα email, στο ηλεκτρονικό προσκλητήριο.`,
        `Ο κωδικός είναι πιο βολικός εκεί που δεν υπάρχει οθόνη να πατήσει κανείς: πάνω σε ένα τραπέζι, σε μια πινακίδα, σε ένα τυπωμένο καρτελάκι.`,
        `Οι περισσότεροι γάμοι χρησιμοποιούν και τα δύο, για τον ίδιο προορισμό.`,
      ],
    },
    {
      heading: "Και τα βίντεο;",
      content: [
        `Τα βίντεο των καλεσμένων είναι συχνά ό,τι πιο ζωντανό μένει από μια βραδιά — η είσοδος, ο χορός, μια ευχή που ειπώθηκε στο τραπέζι.`,
        `Είναι όμως και πολύ πιο βαριά αρχεία από τις φωτογραφίες.`,
        `Αν σας ενδιαφέρουν, ελέγξτε από πριν τι υποστηρίζει η υπηρεσία που θα χρησιμοποιήσετε: αν δέχεται βίντεο, αν υπάρχει όριο διάρκειας ή μεγέθους και σε τι ποιότητα κρατούνται.`,
        `Και πείτε το στους καλεσμένους. Πολλοί δεν θα σκεφτούν καν ότι μπορούν να ανεβάσουν βίντεο αν δεν τους το πείτε.`,
      ],
    },
    {
      heading: "Ιδιωτικότητα: ποιος βλέπει τι",
      content: [
        `Είναι η ερώτηση που αξίζει να κάνετε πριν διαλέξετε υπηρεσία, όχι μετά.`,
        `Ρωτήστε:`,
        {
          type: "list",
          items: [
            `Ποιος μπορεί να δει όσα ανεβαίνουν;`,
            `Χρειάζεται κάτι για να μπει κανείς, ή αρκεί ο σύνδεσμος;`,
            `Μπορείτε να αφαιρέσετε μια φωτογραφία;`,
            `Πόσο καιρό παραμένει διαθέσιμο το υλικό;`,
            `Πώς κατεβάζετε στο τέλος όλα τα αρχεία;`,
          ],
        },
        `Ένα QR code σε πινακίδα είναι δημόσιο μέσα στον χώρο — αυτός είναι ο σκοπός του. Το ζήτημα δεν είναι ο κωδικός· είναι το τι επιτρέπει η σελίδα πίσω του σε όποιον φτάσει εκεί.`,
        `Καλό είναι επίσης να πείτε στους καλεσμένους πού καταλήγουν οι φωτογραφίες τους. Δεν χρειάζεται κείμενο όρων· μια πρόταση αρκεί.`,
      ],
    },
    {
      heading: "Ένα απλό σχέδιο για την ημέρα",
      content: [
        `Αν θέλετε να το κλείσετε γρήγορα:`,
        {
          type: "steps",
          items: [
            `Διαλέξτε πού θα καταλήγουν οι φωτογραφίες και τα βίντεο.`,
            `Πάρτε τον σύνδεσμο και τον κωδικό του.`,
            `Δοκιμάστε τα και τα δύο, σε δύο διαφορετικά κινητά.`,
            `Τυπώστε τον κωδικό με μία σαφή πρόταση δίπλα του.`,
            `Βάλτε τον σε δύο ή τρία σημεία, ένα από αυτά στα τραπέζια.`,
            `Στείλτε τον σύνδεσμο και την επόμενη μέρα, με ένα ευχαριστώ.`,
          ],
        },
        `Το τελευταίο βήμα είναι αυτό που ξεχνιέται πιο συχνά, και είναι εκείνο που φέρνει τις φωτογραφίες όσων δεν πρόλαβαν.`,
      ],
    },
    {
      heading: "Οι αναμνήσεις των καλεσμένων, σε ένα μέρος",
      content: [
        `Στο Agapaé, το άλμπουμ φωτογραφιών και βίντεο των καλεσμένων συνδέεται με τον ίδιο τον γάμο σας.`,
        `Οι καλεσμένοι φτάνουν σε αυτό από το QR code ή από τον σύνδεσμο του γάμου σας, ανεβάζουν φωτογραφίες και βίντεο, και όλα συγκεντρώνονται στο ίδιο σημείο — όχι σε δημόσια ροή, αλλά στον ιδιωτικό χώρο της εκδήλωσής σας.`,
        `Το πακέτο ορίζει τα όρια: πόσοι καλεσμένοι μπορούν να ανεβάσουν, πόσα χωράει το άλμπουμ, για πόσο μένει ανοιχτό το ανέβασμα και αν οι καλεσμένοι μπορούν να ανεβάσουν και βίντεο.`,
        {
          type: "note",
          text: `Το άλμπουμ των καλεσμένων υπάρχει σε όλα τα πακέτα του Agapaé, με διαφορετικά όρια στο καθένα.`,
        },
        {
          type: "p",
          text: [
            {
              text: "Δείτε πώς λειτουργούν οι φωτογραφίες γάμου στο Agapaé",
              href: "/wedding-photo-sharing",
            },
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Τι είναι το QR code γάμου για φωτογραφίες;`,
      a: `Είναι ένας κωδικός που ανοίγει μια συγκεκριμένη διεύθυνση όταν τον σκανάρει κάποιος με την κάμερα του κινητού. Ο κωδικός δεν αποθηκεύει τίποτα από μόνος του — όλα εξαρτώνται από τη σελίδα στην οποία οδηγεί.`,
    },
    {
      q: `Πώς ανεβάζουν οι καλεσμένοι φωτογραφίες με ένα QR code;`,
      a: `Σκανάρουν τον κωδικό με την κάμερα, πατούν την ειδοποίηση που εμφανίζεται, η σελίδα ανοίγει στον browser του κινητού και διαλέγουν αρχεία από τη συλλογή τους. Τι ακριβώς βλέπουν εκεί διαφέρει ανάλογα με την υπηρεσία.`,
    },
    {
      q: `Πού να βάλω το QR code στον γάμο;`,
      a: `Εκεί που οι καλεσμένοι έχουν ήδη το κινητό στο χέρι: στα τραπέζια, σε μια πινακίδα στην είσοδο, κοντά στο μπαρ ή στο βιβλίο ευχών. Δύο ή τρία σημεία με καλό φωτισμό είναι αρκετά.`,
    },
    {
      q: `Μπορούν οι καλεσμένοι να ανεβάσουν και βίντεο;`,
      a: `Εξαρτάται από την υπηρεσία. Ελέγξτε πριν τον γάμο αν δέχεται βίντεο, αν υπάρχει όριο μεγέθους ή διάρκειας και σε τι ποιότητα κρατούνται — και πείτε το στους καλεσμένους, γιατί πολλοί δεν θα το σκεφτούν μόνοι τους.`,
    },
    {
      q: `Τι πρέπει να δοκιμάσω πριν τον γάμο;`,
      a: `Σκανάρετε τον τυπωμένο κωδικό με ένα iPhone και ένα Android, ανεβάστε μια πραγματική φωτογραφία μέχρι το τέλος, δοκιμάστε με δεδομένα κινητού και ζητήστε από κάποιον τρίτο να το κάνει χωρίς βοήθεια. Ελέγξτε και το σήμα στον ίδιο τον χώρο.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["how-to-collect-wedding-photos-from-guests"],

    product: [
      {
        href: "/wedding-photo-sharing",
        label: "Φωτογραφίες γάμου",
        blurb:
          "Συγκεντρώστε τις φωτογραφίες και τα βίντεο των καλεσμένων σας στον ιδιωτικό χώρο του γάμου σας, με QR code ή σύνδεσμο.",
      },
    ],
  },
};

export default weddingQrCodeForPhotosEl;
